// Bridges the web app to the native shell when it's running inside the
// Capacitor-wrapped iOS/Android app. Every export here is a no-op on the web
// (Capacitor.isNativePlatform() is false there), so this file is safe to
// import unconditionally and changes nothing about the browser experience.
import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { API_BASE_URL } from './config';

export const isNativePlatform = () => Capacitor.isNativePlatform();

/** Hides the native splash, tolerating a missing plugin. */
async function hideSplash() {
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch { /* plugin not installed yet (web-only checkout) */ }
}

// Dead-man's switch for the splash screen.
//
// launchAutoHide is false, so the splash is dismissed by exactly one thing:
// setupNativeChrome() reaching SplashScreen.hide() from inside a React effect.
// That is a single point of failure with no visible symptom -- if the app
// throws while mounting, or the effect never runs, the splash simply stays up
// forever and the app looks like it is loading. No error surfaces, because
// nothing has gone wrong as far as the shell is concerned.
//
// Called at module load rather than from a component, so it does not depend on
// React mounting at all. The timeout is long enough that the normal path
// always wins the race, and hide() is safe to call twice.
if (Capacitor.isNativePlatform()) {
  setTimeout(hideSplash, 6000);
}

// Sets the native status bar color/style and hides the native splash screen
// once React has mounted (the app's own <BootScreen /> takes over from there,
// so there's no blank gap between splash and first paint).
export async function setupNativeChrome() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    // The app bar was drawing underneath the system status bar, so the store
    // logo and search sat behind the clock and battery icons.
    //
    // The CSS was already correct -- the header pads itself by
    // env(safe-area-inset-top) and index.html sets viewport-fit=cover. What
    // was missing is the other half: nothing ever told the native side
    // whether the WebView is supposed to extend under the bar. Since
    // targetSdk 35, Android enforces edge-to-edge, so the WebView now goes
    // full-bleed by default and the inset has to be taken back deliberately.
    //
    // Declaring overlay:false is the reliable half of the pair, because it
    // does not depend on env() reporting a real number inside an Android
    // WebView -- the native layer insets the view itself. With the view
    // inset, safe-area-inset-top resolves to 0, so the CSS padding above
    // collapses rather than doubling up.
    //
    // It must be set before the colour: on Android the bar has no colour to
    // paint while it is still an overlay.
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#010D21' });
    }
    // Style names describe the *text*, not the background: per the plugin's own
    // Style enum, Dark is "light text for dark backgrounds" and Light is "dark
    // text for light backgrounds". The bar is brand navy, so its icons must be
    // pale, which is Style.Dark. This read Style.Light -- the comment said what
    // was wanted and the value did the opposite, leaving dark icons on a
    // near-black bar with the clock and battery barely legible.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch { /* plugin not installed yet (web-only checkout) */ }
  await hideSplash();
}

// Maps the Android hardware/gesture back button to the app's own history
// stack instead of the OS default (which would otherwise back out of the
// WebView entirely on the first press). Call once from inside <App />, after
// `navigate` and `location` are available from react-router.
export function useHardwareBackButton(navigate, location) {
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let listenerHandle;
    let cancelled = false;

    import('@capacitor/app').then(({ App: CapApp }) => {
      if (cancelled) return;
      CapApp.addListener('backButton', () => {
        if (locationRef.current.pathname === '/') {
          CapApp.exitApp();
        } else {
          navigate(-1);
        }
      }).then((handle) => { listenerHandle = handle; });
    }).catch(() => { /* plugin not installed yet (web-only checkout) */ });

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [navigate]);
}

// Routes an incoming App Links URL into the SPA.
//
// MainActivity is launchMode="singleTask", so a link tapped while the app is
// already running is delivered to the live instance rather than starting a new
// one. Capacitor surfaces that as `appUrlOpen`. With no listener the app simply
// comes to the foreground on whatever page it was last showing, which to the
// person who tapped the link is indistinguishable from the link being ignored.
//
// Only the path is taken from the incoming URL, never the origin: App.js
// derives its page from location.pathname (see the big IIFE there), and the
// host is already guaranteed by the intent filter's android:host. Parsing
// defensively anyway -- a malformed URL must not throw inside a listener,
// where nothing would catch it.
export function useDeepLinks(navigate) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let listenerHandle;
    let cancelled = false;

    import('@capacitor/app').then(({ App: CapApp }) => {
      if (cancelled) return;
      CapApp.addListener('appUrlOpen', ({ url }) => {
        try {
          const { pathname, search, hash } = new URL(url);
          navigate(`${pathname}${search}${hash}`);
        } catch { /* not a URL we can route -- leave the app where it is */ }
      }).then((handle) => { listenerHandle = handle; });
    }).catch(() => { /* plugin not installed yet (web-only checkout) */ });

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [navigate]);
}

// Registers this device for push and hands the FCM token to the server.
//
// The website's push path (a service worker plus a W3C PushSubscription) does
// not work here at all: Android WebViews do not implement the Web Push API, so
// before this the packaged app could never register, and every order-status
// push quietly skipped the users who had installed it.
//
// Deliberately NOT called at startup. Android 13+ shows a system permission
// dialog for notifications, and a dialog fired at first launch -- before the
// person has any reason to want one -- is the reliable way to get a permanent
// denial. Call this after an order is placed, where "tell me when it ships" is
// something they actually want. `enabled` gates it so the caller decides when.
export function usePushRegistration(enabled) {
  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return undefined;
    let handles = [];
    let cancelled = false;

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        if (cancelled) return;

        // checkPermissions first so an already-granted device is not asked
        // again, and a previously denied one is not nagged on every order.
        let status = await PushNotifications.checkPermissions();
        if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
          status = await PushNotifications.requestPermissions();
        }
        if (status.receive !== 'granted' || cancelled) return;

        // The token arrives asynchronously via the registration event -- it is
        // not the return value of register().
        handles.push(await PushNotifications.addListener('registration', async ({ value }) => {
          try {
            await fetch(`${API_BASE_URL}/api/notifications/push/device`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ token: value }),
            });
          } catch { /* offline -- re-registers on the next successful order */ }
        }));

        handles.push(await PushNotifications.addListener('registrationError', (err) => {
          // Almost always a missing or mismatched google-services.json.
          console.warn('[push] device registration failed:', err?.error || err);
        }));

        // Tapping a notification carries the `url` the server put in the FCM
        // data payload, so the app opens the order rather than the home page.
        handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
          const url = notification?.data?.url;
          if (typeof url === 'string' && url.startsWith('/')) window.location.assign(url);
        }));

        await PushNotifications.register();
      } catch {
        // Plugin not installed (web-only checkout, or an older APK) -- the
        // website's own push keeps working and nothing here is required.
      }
    })();

    return () => {
      cancelled = true;
      handles.forEach((h) => h?.remove?.());
    };
  }, [enabled]);
}
